/**
 * Contextual help : pure functions over the help manifest (help/<lang>/index.json, produced
 * by tools/doc-build/publish-help.js). No DOM, no fetch : the panel (helpPanel.js) loads the
 * files and renders, this module only decides WHAT to show.
 *
 * Manifest shape (spec docs/superpowers/specs/2026-10-09-aide-contextuelle-design.md § 3.1) :
 *   pages[]    {id, title, module, file}             README order
 *   sections[] {id: "page#anchor", p, t, l, a, s}    document order ; a = "u" | "c" | "uc"
 *   screens    {"#code": [section idx…]}
 *   families   {"#prefix-": [section idx…]}
 *   toc        [{title, children: [...] | page}]
 */

/**
 * Help language for a user language : the documentation exists in French and English only
 *
 * @param {string} lang the i18n language (fr, fr-FR, en-GB, vi…)
 * @return {string} "fr" or "en"
 */
function helpLang(lang){
    return String(lang || "fr").substring(0, 2).toLowerCase() === "fr" ? "fr" : "en" ;
}

/**
 * Sections documenting exactly this screen code, or else those of the families the code
 * belongs to (ex: "#iz-doc-" for "#iz-doc-invoices")
 *
 * @param {object} index the manifest
 * @param {string} code a screen code ("#route")
 * @return {object|null} {via: "direct"|"family", sections: [idx…]} (document order) or null
 */
function lookup(index, code){
    const direct = (index.screens || {})[code] ;
    if(direct && direct.length){ return {via: "direct", sections: direct.slice()} ; }
    const families = index.families || {} ;
    const found = new Set() ;
    Object.keys(families).forEach((prefix) => {
        if(code.indexOf(prefix) === 0){
            families[prefix].forEach((i) => found.add(i)) ;
        }
    }) ;
    if(!found.size){ return null ; }
    return {via: "family", sections: Array.from(found).sort((a, b) => a - b)} ;
}

/**
 * Section indexes of a screen code (exact code, else families)
 *
 * @return {number[]} empty if none
 */
function sectionsForCode(index, code){
    const hit = lookup(index, code) ;
    return hit ? hit.sections : [] ;
}

/**
 * Form → list fallback (decision H13) : the documentation often describes the list screen
 * only, while the user opens the record form. "#x-form" → "#x", then "#xs" (list route
 * derived from the table name, ex: "#purchase-order-form" → "#purchase-orders"). Naming
 * rule only, same as tools/doc-build/lib/coverage.js#formFallbacks.
 *
 * @param {string} code a screen code
 * @return {string[]} candidate list codes (empty if the code is not a form)
 */
function formFallbacks(code){
    const match = /^(#.+)-form$/.exec(code) ;
    return match ? [match[1], match[1]+"s"] : [] ;
}

/**
 * Resolve the help of the screens currently displayed (spec § 4.2, same results as
 * tools/doc-build/lib/coverage.js#resolveScreens)
 *
 * The top of the stack wins (a popup over a list, a form over a list) ; an undocumented form
 * falls back on its list BEFORE going down the stack ; then down the stack until a documented
 * screen is found. The screens further down are proposed as "other screens", once per help
 * code.
 *
 * @param {string[]} routeNames AppController.getActiveRouteNames() : bottom to top, with or without "#"
 * @param {object} index the manifest
 * @return {{primary: object|null, others: object[]}} entries {code, route, via, sections, depth} :
 *  code = the code carrying the help (the list on a fallback), route = the displayed route,
 *  via = "direct" | "family" | "fallback", depth = 0 for the top of the stack
 */
function resolveScreens(routeNames, index){
    const candidates = [] ;
    (routeNames || []).slice().reverse().forEach((name) => {
        const route = "#"+String(name || "").replace(/^#/, "") ;
        if(route !== "#" && candidates.indexOf(route) === -1){ candidates.push(route) ; }
    }) ;
    const found = [] ;
    candidates.forEach((route, depth) => {
        let hit = lookup(index, route) ;
        let code = route ;
        if(!hit){
            const lists = formFallbacks(route) ;
            for(let i=0; i<lists.length; i++){
                const listHit = lookup(index, lists[i]) ;
                if(listHit){
                    hit = {via: "fallback", sections: listHit.sections} ;
                    code = lists[i] ;
                    break ;
                }
            }
        }
        if(hit && !found.some((f) => f.code === code)){
            found.push({code: code, route: route, via: hit.via, sections: hit.sections, depth: depth}) ;
        }
    }) ;
    return {primary: found[0] || null, others: found.slice(1)} ;
}

/**
 * Is the section for the end user (decision D2 : anything not consultant-only)
 */
function isUserSection(index, sectionIdx){
    const section = index.sections[sectionIdx] ;
    return !!section && String(section.a || "").indexOf("u") !== -1 ;
}

/**
 * Group the sections of a screen by page, audiences apart (decision D2)
 *
 * @param {object} index the manifest
 * @param {number[]} sectionIdxs section indexes
 * @return {object[]} [{page, user: [idx…], consultant: [idx…]}], pages in README order,
 *  sections in document order
 */
function groupByPage(index, sectionIdxs){
    const byPage = new Map() ;
    Array.from(new Set(sectionIdxs || [])).sort((a, b) => a - b).forEach((i) => {
        const section = index.sections[i] ;
        if(!section){ return ; }
        if(!byPage.has(section.p)){
            byPage.set(section.p, {page: section.p, user: [], consultant: []}) ;
        }
        byPage.get(section.p)[isUserSection(index, i) ? "user" : "consultant"].push(i) ;
    }) ;
    return Array.from(byPage.values()).sort((a, b) => a.page - b.page) ;
}

/**
 * What the panel shows in "screen" mode
 *
 * @param {string[]} routeNames AppController.getActiveRouteNames()
 * @param {object} index the manifest
 * @return {object} {primary, others, groups, consultantOpen} ; consultantOpen is true when no
 *  user section documents the screen (a settings screen would otherwise be all folded)
 */
function screenHelp(routeNames, index){
    const resolved = resolveScreens(routeNames, index) ;
    const groups = resolved.primary ? groupByPage(index, resolved.primary.sections) : [] ;
    return {
        primary: resolved.primary,
        others: resolved.others,
        groups: groups,
        consultantOpen: groups.length > 0 && groups.every((g) => g.user.length === 0),
    } ;
}

/**
 * Lower case, without accents nor punctuation
 */
function normalize(text){
    return String(text || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
        .toLowerCase().replace(/[^a-z0-9]+/g, " ").trim() ;
}

/**
 * Search the section titles (case and accents ignored, words in any order). The page title
 * counts too, so "réception" finds every section of the receipts page ; sections whose own
 * title matches come first.
 *
 * @param {object} index the manifest
 * @param {string} query the searched text
 * @param {number} [limit] max results (20)
 * @return {number[]} section indexes
 */
function searchTitles(index, query, limit){
    const words = normalize(query).split(" ").filter(Boolean) ;
    if(!words.length){ return [] ; }
    const max = limit || 20 ;
    const own = [] ;
    const byPage = [] ;
    index.sections.forEach((section, i) => {
        const title = normalize(section.t) ;
        if(words.every((w) => title.indexOf(w) !== -1)){
            own.push(i) ;
            return ;
        }
        const page = index.pages[section.p] ;
        const both = title+" "+normalize(page && page.title) ;
        if(words.every((w) => both.indexOf(w) !== -1)){
            byPage.push(i) ;
        }
    }) ;
    return own.concat(byPage).slice(0, max) ;
}

/**
 * Resolve a documentation reference
 *
 * @param {object} index the manifest
 * @param {string} ref "page#anchor" (data-help-ref) or "page"
 * @return {object|null} {page: idx, anchor: string|null}
 */
function parseRef(index, ref){
    if(!ref){ return null ; }
    const sharp = String(ref).indexOf("#") ;
    const pageId = sharp === -1 ? ref : ref.substring(0, sharp) ;
    const anchor = sharp === -1 ? "" : ref.substring(sharp+1) ;
    const page = index.pages.findIndex((p) => p.id === pageId) ;
    if(page === -1){ return null ; }
    return {page: page, anchor: anchor || null} ;
}

module.exports = {
    helpLang,
    lookup,
    sectionsForCode,
    formFallbacks,
    resolveScreens,
    isUserSection,
    groupByPage,
    screenHelp,
    normalize,
    searchTitles,
    parseRef,
} ;
